/**
 * `/play`: the Play hub (docs/design/06 §5, 05 §8): pick one of your decks (eligibility per tier from
 * play's own validator), a rules tier (your default from settings), then the casual queue, a private room
 * by link, an invite to a friend (mutual follows), or a practice match against the bot (owner decision
 * Q50). `?deck=<id>` pre-selects the deck Runebound's "Play this deck" sent.
 *
 * Before anything starts, the "Ready to play" card (QA batch 3 #1, `features/play/readiness.ts`) sums up
 * the next match (deck, tier, automation, clock, opponent) and flags what is missing or illegal with its
 * fix. The hub's choices (tier, opponent, bot deck and level) and the deck are kept on this device, so a
 * trip to runebound.gg to fix the deck comes back to the same setup.
 */
import {
  BAR_BORDER,
  Field,
  Frame,
  GhostButton,
  GhostLink,
  LOOK,
  Notice,
  PageHead,
  PrimaryButton,
  SIDE_LOOK,
  SURFACE,
  Segmented,
  Sheet,
  StatusBadge,
  WELL,
  inputClass,
  inputStyle,
} from '@play/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { Bot, ExternalLink, FlaskConical, Layers, Link2, Loader2, Search, Swords, UserPlus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/auth';
import { SignInGate } from '../auth/SignInPanel';
import { RUNEBOUND_URL, SHEET_CLASS } from '../components/site';
import { DeckTile } from '../features/play/DeckTile';
import {
  keepDeckSelection,
  rememberLastDeck,
  readLastDeck,
  useDeckCheck,
  useDecksLegality,
  useMyDecks,
} from '../features/play/decks';
import { sendInvite, useFriends } from '../features/play/invites';
import { ReadinessCard } from '../features/play/ReadinessCard';
import {
  type DeckState,
  type HubChoices,
  type OpponentKind,
  TIER_LINE,
  automationSummary,
  clockSummary,
  opponentSummary,
  readHubChoices,
  readinessFlags,
  readyToStart,
  saveHubChoices,
} from '../features/play/readiness';
import { enabledTier, TierPicker } from '../features/play/TierPicker';
import { api, failureMessage } from '../lib/api';
import { useCatalog } from '../lib/catalog';
import { cardFace } from '../lib/cards';
import { type DeckStart, bindMatchDeck, rememberPendingDeck } from '../lib/deck-reference';
import { type EnabledTier, env } from '../lib/env';
import { formatClock } from '../lib/format';
import { rememberDeckForPreload } from '../lib/preload';
import { useNow } from '../lib/use-now';
import { useSettings } from '../settings/settings-store';

const playApi = getRouteApi('/shell/play');

/** `/play` (signed out: sign in first, then come back with the same deck). */
export function PlayPage() {
  const { user, loading } = useAuth();
  const search = playApi.useSearch();
  if (loading) return null;
  if (!user) {
    return (
      <SignInGate
        title="Sign in to play"
        next={search.deck ? `/play?deck=${encodeURIComponent(search.deck)}` : '/play'}
      >
        Your decks from runebound.gg, the casual queue, rooms and practice games.
      </SignInGate>
    );
  }
  return <PlayHub preselect={search.deck ?? null} />;
}

type Busy = null | 'queue' | 'room' | 'bot' | 'invite';

/** The hub itself (also the signed-in home). */
export function PlayHub({ preselect = null }: { preselect?: string | null }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const navigate = useNavigate();
  const decks = useMyDecks(user !== null);
  const defaultTier = useSettings((s) => s.profile.defaultTier);
  const settings = useSettings((s) => s.profile.settings);
  const catalog = useCatalog();
  // The hub's choices survive leaving the page (a deck fix on runebound.gg): tier, opponent, bot setup.
  const [stored] = useState<HubChoices>(readHubChoices);
  const [tier, setTier] = useState<EnabledTier>(stored.tier ?? enabledTier(defaultTier));
  const [tierChosen, setTierChosen] = useState(stored.tier !== null);
  const [opponent, setOpponent] = useState<OpponentKind>(stored.opponent);
  const [deckId, setDeckId] = useState<string | null>(preselect ?? readLastDeck());
  // A deck the player chose (a click or `?deck=`) stays; the remembered or default one follows legality.
  const [explicit, setExplicit] = useState(preselect !== null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [queuedSince, setQueuedSince] = useState<number | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Practice (Q50): the bot's deck (a mirror of yours or a preset) and difficulty.
  const [botDeck, setBotDeck] = useState<string>(stored.botDeck);
  const [botLevel, setBotLevel] = useState<'easy' | 'normal'>(stored.botLevel);
  const [presets, setPresets] = useState<{ id: string; name: string }[] | null>(null);
  useEffect(() => {
    api
      .botDecks()
      .then(setPresets)
      .catch(() => {
        setPresets([]);
      });
  }, []);
  useEffect(() => {
    saveHubChoices({ tier: tierChosen ? tier : null, opponent, botDeck, botLevel });
  }, [tier, tierChosen, opponent, botDeck, botLevel]);
  const decksRef = useRef<HTMLDivElement>(null);
  const botDeckRef = useRef<HTMLSelectElement>(null);
  const check = useDeckCheck(deckId);
  // Coming back from runebound.gg (a deck fixed in another tab): check the decks again.
  const refetchDeck = check.refetch;
  const refetchDecks = decks.refetch;
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void refetchDecks();
      if (deckId !== null) void refetchDeck();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [deckId, refetchDeck, refetchDecks]);
  useNow(1000, queuedSince !== null);

  // The profile's default tier (loaded after the first render) applies until the player picks one here.
  useEffect(() => {
    if (!tierChosen) setTier(enabledTier(defaultTier));
  }, [defaultTier, tierChosen]);

  // Keep a valid selection: the chosen deck, else the last one while legal, else the newest legal deck.
  const legality = useDecksLegality(decks.data, tier);
  const autoNext = keepDeckSelection(decks.data, deckId, { explicit, legal: legality });
  useEffect(() => {
    if (autoNext !== deckId) setDeckId(autoNext);
  }, [autoNext, deckId]);

  useEffect(() => {
    if (deckId) rememberLastDeck(deckId);
  }, [deckId]);

  // The queue survives navigation (05 §8): resume polling if we are already queued.
  useEffect(() => {
    void api
      .queueStatus()
      .then((s) => {
        if (s.status === 'queued') setQueuedSince(Date.parse(s.since));
        if (s.status === 'matched') {
          if (userId) bindMatchDeck(userId, { kind: 'queue' }, s.matchId);
          void navigate({ to: '/match/$id', params: { id: s.matchId } });
        }
      })
      .catch(() => undefined);
  }, [navigate, userId]);

  useEffect(() => {
    if (queuedSince === null) return;
    const t = setInterval(() => {
      void api
        .queueStatus()
        .then((s) => {
          if (s.status === 'matched') {
            setQueuedSince(null);
            if (userId) bindMatchDeck(userId, { kind: 'queue' }, s.matchId);
            void navigate({ to: '/match/$id', params: { id: s.matchId } });
          } else if (s.status === 'idle') setQueuedSince(null);
        })
        .catch(() => undefined);
    }, 2000);
    return () => {
      clearInterval(t);
    };
  }, [queuedSince, navigate, userId]);

  const deckState: DeckState =
    deckId === null
      ? { s: 'none' }
      : check.isError
        ? { s: 'error', message: failureMessage(check.error) }
        : check.data
          ? {
              s: 'checked',
              legal: check.data.legal,
              issues: check.data.issues.length,
              playable: check.data.tiers[tier],
              manual: check.data.manual.length,
            }
          : { s: 'loading' };
  const readinessInput = {
    decks: decks.data ? decks.data.length : null,
    deck: deckState,
    tier,
    opponent,
    botDeck,
    presets: presets ? presets.map((p) => p.id) : null,
  };
  const flags = readinessFlags(readinessInput);
  const ready = readyToStart(readinessInput, flags);
  // The table's own deck list (lib/deck-reference.ts): the deck is held for this start, then snapshotted for the
  // exact match id this start leads to (never matched by legend).
  const deckFor = (start: DeckStart) => {
    if (userId && check.data) rememberPendingDeck(userId, start, check.data.deck);
  };
  const toMatch = (start: DeckStart, matchId: string) => {
    if (userId) bindMatchDeck(userId, start, matchId);
    void navigate({ to: '/match/$id', params: { id: matchId } });
  };
  const run = async (what: Exclude<Busy, null>, fn: () => Promise<void>) => {
    setBusy(what);
    setError(null);
    if (check.data) rememberDeckForPreload(check.data.deck);
    try {
      await fn();
    } catch (e) {
      setError(failureMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const selectedDeck = decks.data?.find((d) => d.id === deckId) ?? null;
  const offline = !ready || busy !== null;
  const legend = selectedDeck?.legend_card_id
    ? cardFace(catalog, { cardId: selectedDeck.legend_card_id, printingId: selectedDeck.legend_printing_id })
    : null;
  const championName = selectedDeck?.champion_card_id
    ? (catalog?.card(selectedDeck.champion_card_id)?.name ?? null)
    : null;
  const botDeckName =
    botDeck === 'mirror' ? 'a mirror of your deck' : (presets?.find((p) => p.id === botDeck)?.name ?? 'a preset deck');
  const pickDeck = () => {
    const el = decksRef.current;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  };
  return (
    <div className="flex flex-col">
      <PageHead
        title="Play"
        icon={<Swords size={18} />}
        end={
          <TierPicker
            value={tier}
            onChange={(t) => {
              setTier(t);
              setTierChosen(true);
            }}
          />
        }
      >
        1v1 Standard, best of one. Pick a deck and a rules tier.
      </PageHead>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-4 items-start">
        <div ref={decksRef} className="scroll-mt-[81px]">
          <Frame
            title="Your decks"
            icon={<Layers size={15} color="#b9adff" />}
            end={
              <span className="text-[12.5px] hidden sm:inline" style={{ color: LOOK.muted }}>
                From runebound.gg. Edits there show up here.
              </span>
            }
          >
            <div className="p-4">
              {decks.isPending ? (
                <div className="flex items-center gap-2 py-10 justify-center text-[13px]" style={{ color: LOOK.muted }}>
                  <Loader2 size={16} className="animate-spin motion-reduce:animate-none" /> Loading decks…
                </div>
              ) : decks.isError ? (
                <Notice tone="bad">{failureMessage(decks.error)}</Notice>
              ) : decks.data.length === 0 ? (
                <div className="flex flex-col items-start gap-3 py-4">
                  <p className="text-[13px]" style={{ color: LOOK.sub }}>
                    You don’t have any decks yet. Build one on Runebound, sync it, and it appears here.
                  </p>
                  <GhostLink href={`${RUNEBOUND_URL}/decks/build`} target="_blank" rel="noreferrer">
                    Build a deck <ExternalLink size={14} />
                  </GhostLink>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2.5" role="list" aria-label="Decks">
                  {decks.data.map((d) => (
                    <DeckTile
                      key={d.id}
                      deck={d}
                      selected={d.id === deckId}
                      onSelect={() => {
                        setExplicit(true);
                        setDeckId(d.id);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </Frame>
        </div>

        <div className="flex flex-col gap-4 lg:sticky lg:top-[81px]">
          <Frame
            title="Ready to play"
            icon={<Swords size={15} color="#b9adff" />}
            end={<StatusBadge color={LOOK.violet}>Tier {tier}</StatusBadge>}
          >
            <div className="p-4 flex flex-col gap-4">
              <ReadinessCard
                deckName={selectedDeck?.name ?? null}
                legend={legend}
                legendName={legend?.name ?? null}
                championName={championName}
                counts={
                  selectedDeck
                    ? {
                        main: selectedDeck.main_count,
                        runes: selectedDeck.rune_count,
                        battlefields: selectedDeck.battlefield_count,
                      }
                    : null
                }
                legal={deckState.s === 'checked' ? deckState.legal : deckState.s === 'error' ? false : null}
                issues={check.data?.issues ?? []}
                tier={tier}
                tierLine={TIER_LINE[tier]}
                automation={automationSummary(tier, settings)}
                clock={clockSummary()}
                opponent={opponent}
                opponentLine={opponentSummary({ opponent, botLevel }, botDeckName)}
                flags={flags}
                deckUrl={selectedDeck ? `${RUNEBOUND_URL}/decks/${encodeURIComponent(selectedDeck.id)}/` : null}
                buildUrl={`${RUNEBOUND_URL}/decks/build/?step=legend`}
                onRecheck={() => {
                  void check.refetch();
                  void decks.refetch();
                }}
                onPickDeck={pickDeck}
                onPickBotDeck={() => {
                  setOpponent('bot');
                  setBotDeck('mirror');
                  botDeckRef.current?.focus();
                }}
              />

              <Field label="Opponent">
                <Segmented
                  label="Opponent"
                  value={opponent}
                  onChange={setOpponent}
                  options={[
                    [
                      'human',
                      <span key="h" data-testid="opponent-human">
                        A person
                      </span>,
                    ],
                    [
                      'bot',
                      <span key="b" data-testid="opponent-bot">
                        Practice bot
                      </span>,
                    ],
                  ]}
                />
              </Field>

              {queuedSince !== null ? (
                <Notice tone="info" icon={<Search size={15} />} role="status" data-testid="queue-status">
                  <div className="flex items-center gap-3">
                    <span className="flex-1">
                      Looking for an opponent…{' '}
                      <span className="mono tabular-nums" style={{ color: LOOK.muted }}>
                        {formatClock(Date.now() - queuedSince)}
                      </span>
                    </span>
                    <GhostButton
                      onClick={() => {
                        void api.leaveQueue().finally(() => {
                          setQueuedSince(null);
                        });
                      }}
                    >
                      <X size={14} /> Cancel
                    </GhostButton>
                  </div>
                </Notice>
              ) : opponent === 'human' ? (
                <div className="flex flex-col gap-2">
                  <PrimaryButton
                    big
                    className="w-full"
                    disabled={offline}
                    onClick={() =>
                      void run('queue', async () => {
                        deckFor({ kind: 'queue' });
                        const s = await api.joinQueue({ deckId: deckId ?? '', tier });
                        if (s.status === 'matched') toMatch({ kind: 'queue' }, s.matchId);
                        else if (s.status === 'queued') setQueuedSince(Date.parse(s.since));
                      })
                    }
                    data-testid="play-queue"
                  >
                    {busy === 'queue' ? (
                      <Loader2 size={17} className="animate-spin motion-reduce:animate-none" />
                    ) : (
                      <Swords size={17} />
                    )}
                    Casual queue
                  </PrimaryButton>
                  <div className="grid grid-cols-2 gap-2">
                    <GhostButton
                      className="w-full !h-10"
                      disabled={offline}
                      onClick={() =>
                        void run('room', async () => {
                          const room = await api.createRoom({ tier, deckId: deckId ?? '' });
                          deckFor({ kind: 'room', code: room.code });
                          void navigate({ to: '/r/$code', params: { code: room.code } });
                        })
                      }
                      title="Private room (share a link)"
                    >
                      <Link2 size={15} /> Private room
                    </GhostButton>
                    <GhostButton
                      className="w-full !h-10"
                      disabled={offline}
                      onClick={() => {
                        setInviteOpen(true);
                      }}
                    >
                      <UserPlus size={15} /> Invite a friend
                    </GhostButton>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3" data-testid="practice-options">
                  <div className="grid grid-cols-[minmax(0,1fr)_150px] gap-2">
                    <Field label="Bot deck">
                      <select
                        ref={botDeckRef}
                        value={botDeck}
                        onChange={(e) => {
                          setBotDeck(e.target.value);
                        }}
                        className={inputClass}
                        style={inputStyle}
                        aria-label="Bot deck"
                      >
                        <option value="mirror">Mirror (your deck)</option>
                        {(presets ?? []).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Difficulty">
                      <Segmented
                        label="Bot difficulty"
                        value={botLevel}
                        onChange={setBotLevel}
                        options={[
                          ['easy', 'Easy'],
                          ['normal', 'Normal'],
                        ]}
                      />
                    </Field>
                  </div>
                  <PrimaryButton
                    big
                    className="w-full"
                    disabled={offline}
                    onClick={() =>
                      void run('bot', async () => {
                        deckFor({ kind: 'bot' });
                        const m = await api.botMatch({
                          deckId: deckId ?? '',
                          tier,
                          opponent: botDeck === 'mirror' ? { kind: 'mirror' } : { kind: 'preset', presetId: botDeck },
                          level: botLevel,
                          speed: useSettings.getState().profile.settings.botSpeed,
                        });
                        toMatch({ kind: 'bot' }, m.matchId);
                      })
                    }
                    data-testid="play-bot"
                  >
                    {busy === 'bot' ? (
                      <Loader2 size={17} className="animate-spin motion-reduce:animate-none" />
                    ) : (
                      <Bot size={17} />
                    )}
                    Practice vs bot
                  </PrimaryButton>
                </div>
              )}
              {error ? (
                <Notice tone="bad" role="alert">
                  {error}
                </Notice>
              ) : null}
            </div>
          </Frame>
          {env.sandbox ? (
            <Link
              to="/sandbox"
              className="rounded-[12px] p-4 flex items-center gap-3 transition-[filter] hover:brightness-125"
              style={{ background: SURFACE, boxShadow: `inset 0 0 0 1px ${BAR_BORDER}` }}
            >
              <span
                className="w-9 h-9 rounded-[9px] inline-flex items-center justify-center shrink-0"
                style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
              >
                <FlaskConical size={17} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13.5px] font-semibold" style={{ color: LOOK.text }}>
                  Developer sandbox
                </span>
                <span className="block text-[12px]" style={{ color: LOOK.muted }}>
                  Both seats on one screen, engine in the browser.
                </span>
              </span>
              <StatusBadge color={LOOK.violet}>dev</StatusBadge>
            </Link>
          ) : null}
        </div>
      </div>
      <InviteSheet
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
        }}
        onInvite={(friendId) =>
          run('invite', async () => {
            const room = await api.createRoom({ tier, deckId: deckId ?? '' });
            deckFor({ kind: 'room', code: room.code });
            await sendInvite(friendId, room.code, tier);
            setInviteOpen(false);
            void navigate({ to: '/r/$code', params: { code: room.code } });
          })
        }
        busy={busy === 'invite'}
      />
    </div>
  );
}

function InviteSheet({
  open,
  onClose,
  onInvite,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onInvite: (id: string) => Promise<void>;
  busy: boolean;
}) {
  const { user } = useAuth();
  const friends = useFriends(open ? (user?.id ?? null) : null);
  return (
    <Sheet open={open} onClose={onClose} title="Invite a friend" size="md" className={SHEET_CLASS}>
      <p className="text-[13px] mb-3" style={{ color: LOOK.sub }}>
        Friends are players you follow who follow you back on Runebound.
      </p>
      {friends.isPending ? (
        <p className="text-[13px] flex items-center gap-2" style={{ color: LOOK.muted }}>
          <Loader2 size={14} className="animate-spin motion-reduce:animate-none" /> Loading…
        </p>
      ) : friends.isError ? (
        <Notice tone="bad">{failureMessage(friends.error)}</Notice>
      ) : friends.data.length === 0 ? (
        <p className="text-[13px]" style={{ color: LOOK.muted }}>
          No mutual follows yet. Follow each other on runebound.gg first, or share a private room link.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {friends.data.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 rounded-[10px] px-3 h-12"
              style={{ background: WELL, boxShadow: `inset 0 0 0 1px ${LOOK.border}` }}
            >
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold"
                style={{ background: SIDE_LOOK.you.soft, color: SIDE_LOOK.you.text }}
              >
                {(f.displayName ?? f.handle ?? '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13.5px] truncate" style={{ color: LOOK.text }}>
                  {f.displayName ?? f.handle ?? 'Player'}
                </span>
                {f.handle ? (
                  <span className="block text-[11.5px]" style={{ color: LOOK.muted }}>
                    @{f.handle}
                  </span>
                ) : null}
              </span>
              <PrimaryButton disabled={busy} onClick={() => void onInvite(f.id)}>
                Invite
              </PrimaryButton>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
